<script setup lang="ts">
import { useRouter } from 'vue-router';
import type { RobotSummary } from '@/models/RobotSummary';
import formatDate from '@/helpers/DateFormatterHelper';
import type HealthIndicatorEnum from '@/enums/HealthIndicatorEnum';
import {
  healthColor,
  healthIndicatorText,
} from '@/helpers/HealthIndicatorHelper';

defineProps<{ robot: RobotSummary }>();

const router = useRouter();

function getHealthColor(healthIndicator: HealthIndicatorEnum) {
  return healthColor(healthIndicator);
}

function getHealthString(healthIndicator: HealthIndicatorEnum) {
  return healthIndicatorText(healthIndicator);
}

function doFormatDate(date: string) {
  return formatDate(date, true);
}

function openDetails(robot: RobotSummary) {
  router.push({
    path: '/fleet-management/detail',
    query: { serial_number: robot.serialNumber, internal: 1 },
  });
}
</script>

<template>
  <v-card class="robot-card" @click="openDetails(robot)">
    <div class="robot-card__status-badges">
      <v-tooltip anchor="bottom" location="top">
        <template v-slot:activator="{ props }">
          <v-icon
            class="mr-2"
            :color="robot.isOnline ? 'success' : 'error'"
            icon="mdi-satellite-uplink"
            v-bind="props"
          />
        </template>
        <span v-if="robot.isOnline">{{ $gettext('Online') }}</span>
        <span v-else>
          {{ $gettext('Last seen') }}: {{ doFormatDate(robot.lastSeen) }}
        </span>
      </v-tooltip>
      <v-tooltip anchor="bottom" location="top">
        <template v-slot:activator="{ props }">
          <v-icon
            class="mr-2"
            icon="mdi-alarm-light"
            v-bind="props"
            :color="getHealthColor(robot.overallHealthIndicator)"
          />
        </template>
        <span>
          {{ $gettext('Overall health status') }}:
          {{ $gettext(getHealthString(robot.overallHealthIndicator)) }}
        </span>
      </v-tooltip>
    </div>
    <v-card-title style="max-width: calc(100% - 64px)">{{
      robot.name
    }}</v-card-title>
    <v-card-subtitle>{{ robot.serialNumber }}</v-card-subtitle>
    <v-card-text>
      <div>
        <strong>{{ $gettext('Online status') }}:</strong>
        {{ robot.isOnline ? 'Online' : 'Offline' }}
      </div>
      <div>
        <strong>{{ $gettext('Last seen') }}:</strong>
        {{ doFormatDate(robot.lastSeen) }}
      </div>
      <div>
        <strong>{{ $gettext('Overall health status') }}:</strong>
        {{ getHealthString(robot.overallHealthIndicator) }}
      </div>
    </v-card-text>
  </v-card>
</template>

<style lang="scss" scoped>
.robot-card {
  .v-card-title {
    padding-top: 0;
    padding-bottom: 0;
    margin-top: -12px;
    max-width: calc(100% - 64px);
    margin-right: 68px;
    overflow: initial;
    text-overflow: unset;
    white-space: normal;
    line-height: 1.3rem;
    margin-bottom: 6px;
  }
  &__status-badges {
    display: flex;
    align-items: center;
    justify-content: end;
    margin-right: 8px;
    margin-top: 8px;
  }
}
</style>
